import React, { useState, useEffect } from 'react';
import DashboardHeader from '../components/DashboardHeader';
import { useAuth } from '../context/AuthContext';
import { FaTh, FaList, FaCalendarAlt, FaFileAlt, FaEdit, FaEye, FaTrash, FaMoneyBillAlt } from 'react-icons/fa';
import { Modal } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../App.css';
import client from '../api/client';
import { toast } from 'react-toastify';
import { keepDecimalString, keepDigitsString } from '../Utils/number';
function HospitalDashboard() {
  const { user } = useAuth();

  const [jobRequirements, setJobRequirements] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [professionFilter, setProfessionFilter] = useState('');
  const [professionOptions, setProfessionOptions] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null);
  const [viewingJobId, setViewingJobId] = useState(null);
  const [viewJobData, setViewJobData] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const [viewingDetailsJobId, setViewingDetailsJobId] = useState(null);
  const [jobFormData, setJobFormData] = useState({
    lookingFor: professionOptions.find(p => p.professionTypeName.toLowerCase() === "doctor")?.idProfessionType || "",
    qualification: '',
    experience: '',
    salary: '',
    startDate: '',
    endDate: '',
    startTime: '',
    duration: '',
    totalPerHours: '',
    shiftDetails: '',
    remarks: '',
    jobDetails: '',
    period: 'for a shift',
    suspend: false,
    jobStatus: 'Pending',
    activeStatus: true,
    jobID: null,
  });
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [isQualificationDisabled, setIsQualificationDisabled] = useState(true);
  const [viewType, setViewType] = useState('table'); // 'table' or 'grid'
  const getProfessionName = (id) => {
    const p = professionOptions.find(x => Number(x.idProfessionType) === Number(id));
    return p ? p.professionTypeName : "-";
  };
  const formatINRNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(String(value).replace(/,/g, ''));
    if (Number.isNaN(num)) return '';
  
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };
  const getQualificationName = (idProfession, idQualification) => {
    const q = degrees.find(
      x =>
        Number(x.idProfessional) === Number(idProfession) &&
        Number(x.idProfessionalDegree) === Number(idQualification)
    );
    return q ? q.professionalDegreeName : "-";
  };
  const isToday = (dateStr) => dateStr === new Date().toISOString().split('T')[0];

  const getMinStartTime = () => {
    if (!jobFormData.startDate) return '';
    if (!isToday(jobFormData.startDate)) return '00:00';

    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const getMinEndDate = (startDate) => {
    if (!startDate) return new Date().toISOString().split('T')[0];
    const nextDay = new Date(new Date(startDate).getTime() + 24 * 60 * 60 * 1000);
    return nextDay.toISOString().split('T')[0];
  };

  // Records per page based on view type
  const recordsPerPage = viewType === 'grid' ? 9 : 10;

  // Fetch profession options (dropdown)
  useEffect(() => {
    client.get('/api/professional-types')
      .then((response) => {
        const list = response.data?.data || [];
        setProfessionOptions(list);

        // ⭐ Default Doctor ONLY FOR FORM (Add Job)
        const doctor = list.find(
          (p) => (p.professionTypeName || '').toLowerCase() === 'doctor'
        );

        if (doctor) {
          setJobFormData(prev => ({
            ...prev,
            lookingFor: prev.jobID ? prev.lookingFor : doctor.idProfessionType,
          }));
        }

        // ❗ Do NOT set professionFilter here → keeps "All Professions"
      })
      .catch(() => {
        setProfessionOptions([]);
      });
  }, []);


  useEffect(() => {
    client.get('/api/professional-degrees')
      .then((response) => {
        setDegrees(response.data?.data || []);
      })
      .catch(() => {
        setDegrees([]);
      });
  }, []);

  // Update qualification options when profession changes
  // Whenever lookingFor changes (including on edit), filter qualifications
  useEffect(() => {
    const filtered = degrees
      .filter(deg => Number(deg.idProfessional) === Number(jobFormData.lookingFor))
      .map(deg => ({ id: deg.idProfessionalDegree, name: deg.professionalDegreeName }));

    setQualificationOptions(filtered);
    setIsQualificationDisabled(filtered.length === 0);

    // Only set the value if current jobFormData.qualification exists in filtered
    if (filtered.length > 0 && !filtered.some(q => q.id === jobFormData.qualification)) {
      setJobFormData(prev => ({ ...prev, qualification: filtered[0].id }));
    }
  }, [jobFormData.lookingFor, degrees]);



  // Fetch job listings based on filters
  const fetchJobListings = async (professionId, fromDate) => {
    setIsLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (professionId) params.idProfessionType = professionId;
      if (fromDate) params.dateFrom = fromDate;

      const response = await client.get('/api/joblistings/list', { params });
      const list = response.data?.data || [];
      setJobRequirements(list);
      setFilteredData(list);
      setCurrentPage(1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || 'Unable to load job listings.');
      setJobRequirements([]);
      setFilteredData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date for display (DD.MMM.YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {    

        case 'assigned':      
        return 'badge bg-success';     
      case 'open':
        return 'badge bg-primary';
      case 'suspended':      
        return 'badge bg-warning text-dark';
      case 'rejected':
        return 'badge bg-danger text-dark';
         case 'deleted':
        return 'badge bg-danger text-dark';
        case 'completed':
          return 'badge bg-info text-dark';
      default:
        return 'badge bg-secondary';
    }
  };

  // Apply filter -> call API
  const handleApplyFilter = () => {
    fetchJobListings(professionFilter, dateFrom);
  };

  // Get current page data
  const getCurrentPageData = () => {
    const itemsPerPage = viewType === 'grid' ? 9 : 10;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  // Calculate total pages
  const totalPages = Math.ceil(filteredData.length / (viewType === 'grid' ? 9 : 10));

  // Handle pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };



  // Open Edit Mode
  const handleEditJob = async (jobId) => {
    setEditingJobId(jobId);
    try {
      const response = await client.get(`/api/joblistings/${jobId}`);
      const data = response.data?.data;
      if (!data) throw new Error('Job not found');

      const isShift = data.jobDurationType === 'for a shift';
      const dateFrom = data.dateFrom ? data.dateFrom.split('T')[0] : '';
      const dateTo = data.dateTo ? data.dateTo.split('T')[0] : '';
      const timeFrom = data.timeFrom ? data.timeFrom.substring(0, 5) : '';

      setJobFormData((prev) => ({
        ...prev,
        jobID: data.jobID ?? jobId,
        lookingFor: data.idProfessionType || '',
        qualification: data.idQualification || '',
        experience: data.experienceInYears ?? '',
        salary: data.hourlyRateByHospital ?? '',
        startDate: dateFrom,
        endDate: isShift ? '' : dateTo,
        startTime: isShift ? timeFrom : '',
        duration: isShift ? data.durationInHours ?? '' : '',
        totalPerHours: isShift ? (data.durationInHours && data.hourlyRateByHospital ? data.durationInHours * data.hourlyRateByHospital : '') : (data.periodTotalAmountByHospital ?? ''),
        period: isShift ? 'for a shift' : 'for a period',
        remarks: data.remarks || '',
        jobStatus: data.jobStatus || 'Pending',
        activeStatus: data.activeStatus ?? true,
      }));



      setShowEditJobModal(true);
    } catch (err) {
      alert(err?.response?.data?.message || 'Unable to load job details.');
      setEditingJobId(null);
    }
  };

  // Open View Details Modal
  const handleViewJobDetails = async (jobId) => {
    setViewingDetailsJobId(jobId);
    setIsViewLoading(true);
    setViewError('');
    setViewJobData(null);
    setShowViewDetailsModal(true);
    try {
      const response = await client.get(`/api/joblistings/${jobId}`);
      const data = response.data?.data;      
      if (!data) throw new Error('Job not found');
      setViewJobData(data);
    } catch (err) {
      setViewError(err?.response?.data?.message || 'Unable to load job details.');
    } finally {
      setIsViewLoading(false);
    }
  };

  // Handle Close View Details Modal
  const handleCloseViewDetailsModal = () => {
    setShowViewDetailsModal(false);
    setViewingDetailsJobId(null);
    setViewJobData(null);
    setViewError('');
    setIsViewLoading(false);
  };

  // Get responses for a job (dummy data)
  const getJobResponses = (jobId) => {
    const job = jobRequirements.find(j => j.id === jobId);
    if (job && job.responses && Array.isArray(job.responses)) {
      return job.responses; // Return the responses directly from the job object
    }
    return [];
  };

  // Handle Close Responses Modal
  const handleCloseResponsesModal = () => {
    setShowResponsesModal(false);
    setViewingJobId(null);
  };

  // Handle Delete Job
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job requirement?")) {
      return;
    }

    try {
      const response = await client.delete(`/api/joblistings/${jobId}`, {
        headers: { accept: "text/plain" }
      });

      if (response.status === 200) {
        toast.success("Job requirement deleted successfully!");

        fetchJobListings(professionFilter, dateFrom);
      } else {
        toast.error("Failed to delete job. Please try again.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete job. Please try again.");
    }
  };


  // Handle Add Job Requirement
  const handleAddJobClick = () => {
    const doctor = professionOptions.find(
      p => (p.professionTypeName || "").toLowerCase() === "doctor"
    );

    setJobFormData({
      lookingFor: doctor ? doctor.idProfessionType : "",
      qualification: "",
      experience: "",
      salary: "",
      startDate: "",
      endDate: "",
      startTime: "",
      duration: "",
      totalPerHours: "",
      shiftDetails: "",
      remarks: "",
      jobDetails: "",
      period: "for a shift",
      suspend: false,
      jobStatus: "Pending",
      activeStatus: true,
      jobID: null,
    });

    setShowAddJobModal(true);
  };

  // Handle Job Form Change
  const handleJobFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox
    if (type === 'checkbox') {
      setJobFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Experience: digits only, clamped 0–15
    if (name === 'experience') {
      const digits = keepDigitsString(value);
      if (digits === '') {
        setJobFormData(prev => ({ ...prev, experience: '' }));
        return;
      }
      let numValue = parseInt(digits, 10);
      if (Number.isNaN(numValue) || numValue < 0) {
        numValue = 0;
      }
      if (numValue > 15) {
        numValue = 15;
      }
      setJobFormData(prev => ({ ...prev, experience: numValue }));
      return;
    }

    // Decimal numeric fields: salary, duration, totalPerHours
    if (['duration', 'salary', 'totalPerHours'].includes(name)) {
      const cleaned = keepDecimalString(value);
      setJobFormData((prev) => {
        const updated = { ...prev, [name]: cleaned };
        // Auto-calc total for shift
        if (updated.period === 'for a shift') {
          const hourly = parseFloat(updated.salary) || 0;
          const dur = parseFloat(updated.duration) || 0;
          updated.totalPerHours = hourly > 0 && dur > 0 ? String(hourly * dur) : '';
        }
        return updated;
      });
      return;
    }

    // Handle select numeric ids
    if (['lookingFor', 'qualification'].includes(name)) {
      setJobFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
      return;
    }

    // Validation for shiftDetails (max 100 chars)
    if (name === 'shiftDetails') {
      setJobFormData(prev => ({ ...prev, [name]: value.slice(0, 100) }));
      return;
    }

    // Validation for remarks (max 1000 chars)
    if (name === 'remarks') {
      setJobFormData(prev => ({ ...prev, [name]: value.slice(0, 1000) }));
      return;
    }

    setJobFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle Job Form Submit
  const handleJobFormSubmit = async (e) => {
    e.preventDefault();

    const isShift = jobFormData.period === 'for a shift';

    // Validation
    if (!jobFormData.lookingFor || !jobFormData.qualification || !jobFormData.startDate) {
      toast.error('Please fill in all required fields!');
      return;
    }

    if (!isShift && !jobFormData.endDate) {
      alert('End date is required for a period.');
      return;
    }

    if (!isShift) {
      const start = new Date(jobFormData.startDate);
      const end = new Date(jobFormData.endDate);
      if (!(end > start)) {
        toast.error('End date must be after start date.');
        return;
      }
    }

    // Validate experience range (0-15)
    const experience = Number(jobFormData.experience);
    if (jobFormData.experience !== '' && (Number.isNaN(experience) || experience < 0 || experience > 15)) {
      toast.error('Average Years of Experience must be between 0 and 15!');
      return;
    }

    if (isShift) {

      const today = new Date().toISOString().split('T')[0];

      if (jobFormData.startDate === today) {
        if (!jobFormData.startTime) {
          toast.error('Start Time is required.');
          return;
        }

        const now = new Date();
        const [h, m] = jobFormData.startTime.split(':').map(Number);

        const selected = new Date();
        selected.setHours(h, m, 0, 0);

        if (selected < now) {
          toast.error('Start Time must be current time or a future time.');
          return;
        }
      }
      const duration = Number(jobFormData.duration);
      const hourly = Number(jobFormData.salary);
      if (Number.isNaN(duration) || duration <= 0) {
        alert('Duration must be greater than 0.');
        return;
      }
      if (Number.isNaN(hourly) || hourly <= 0) {
        alert('Hourly amount must be greater than 0.');
        return;
      }
    } else {
      const total = Number(jobFormData.totalPerHours);
      if (Number.isNaN(total) || total <= 0) {
        alert('Total amount must be greater than 0 for a period.');
        return;
      }
    }

    const formatTime = (t) => (t ? `${t}:00` : null);
    const payload = {
      idProfessionType: jobFormData.lookingFor || null,
      hospitalID: user?.hospitalId || null,
      idQualification: jobFormData.qualification || null,
      experienceInYears: jobFormData.experience === '' ? null : Number(jobFormData.experience),
      jobDurationType: isShift ? 'for a shift' : 'for a period',
      dateFrom: jobFormData.startDate || null,
      dateTo: isShift ? null : jobFormData.endDate || null,
      timeFrom: isShift ? formatTime(jobFormData.startTime) : null,
      durationInHours: isShift ? (jobFormData.duration === '' ? null : Number(jobFormData.duration)) : null,
      hourlyRateByHospital: isShift ? (jobFormData.salary === '' ? null : Number(jobFormData.salary)) : 0,
      periodTotalAmountByHospital: jobFormData.totalPerHours === '' ? null : Number(jobFormData.totalPerHours),
      jobStatus: 'Pending',
      activeStatus: true,
      remarks: jobFormData.remarks || '',
    };

    try {
      await client.post('/api/joblistings', payload);
      toast.success('Job requirement added successfully!');
      handleCloseModal();
      fetchJobListings(professionFilter, dateFrom);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add job requirement.');
    }

    // Reset form and close modal
    // Refresh handled in success path
  };

  // Handle Close Modal
  const handleCloseModal = () => {
    setShowAddJobModal(false);
    setShowEditJobModal(false);
    setEditingJobId(null);
    setJobFormData({
      lookingFor: '',
      qualification: '',
      experience: '',
      salary: '',
      startDate: '',
      endDate: '',
      startTime: '',
      duration: '',
      totalPerHours: '',
      shiftDetails: '',
      remarks: '',
      jobDetails: '',
      period: 'for a shift',
      suspend: false
    });
    setQualificationOptions([]);
    setIsQualificationDisabled(true);
  };

  // Handle Edit Job Form Submit
  const handleEditJobFormSubmit = async (e) => {
    e.preventDefault();

    const isShift = jobFormData.period === 'for a shift';

    // Validation
    if (!jobFormData.lookingFor || !jobFormData.qualification || !jobFormData.startDate) {
      toast.error('Please fill in all required fields!');
      return;
    }

    if (!isShift && !jobFormData.endDate) {
      toast.error('End date is required for a period.');
      return;
    }

    if (!isShift) {
      const start = new Date(jobFormData.startDate);
      const end = new Date(jobFormData.endDate);
      if (!(end > start)) {
        toast.error('End date must be after start date.');
        return;
      }
    }

    // Validate experience range (0-15)
    const experience = Number(jobFormData.experience);
    if (jobFormData.experience !== '' && (Number.isNaN(experience) || experience < 0 || experience > 15)) {
      toast.error('Average Years of Experience must be between 0 and 15!');
      return;
    }

    if (isShift) {
      const duration = Number(jobFormData.duration);
      const hourly = Number(jobFormData.salary);
      if (Number.isNaN(duration) || duration <= 0) {
        toast.error('Duration must be greater than 0.');
        return;
      }
      if (Number.isNaN(hourly) || hourly <= 0) {
        toast.error('Hourly amount must be greater than 0.');
        return;
      }
    } else {
      const total = Number(jobFormData.totalPerHours);
      if (Number.isNaN(total) || total <= 0) {
        toast.error('Total amount must be greater than 0 for a period.');
        return;
      }
    }
    const formatTime = (t) => (t ? `${t}:00` : null);
    const payload = {
      jobID: jobFormData.jobID ?? editingJobId,
      idProfessionType: jobFormData.lookingFor || null,
      hospitalID: user?.hospitalId || null,
      idQualification: jobFormData.qualification || null,
      experienceInYears: jobFormData.experience === '' ? null : Number(jobFormData.experience),
      jobDurationType: isShift ? 'for a shift' : 'for a period',
      dateFrom: jobFormData.startDate || null,
      dateTo: isShift ? null : jobFormData.endDate || null,
      timeFrom: isShift ? formatTime(jobFormData.startTime) : null,
      durationInHours: isShift ? (jobFormData.duration === '' ? null : Number(jobFormData.duration)) : null,
      hourlyRateByHospital: isShift ? (jobFormData.salary === '' ? null : Number(jobFormData.salary)) : 0,
      periodTotalAmountByHospital:  (jobFormData.totalPerHours === '' ? null : Number(jobFormData.totalPerHours)),
      jobStatus: jobFormData.jobStatus || 'Pending',
      activeStatus: jobFormData.activeStatus ?? true,
      remarks: jobFormData.remarks || '',
    };

    try {
      await client.put('/api/joblistings', payload);
      toast.success('Job requirement updated successfully!');
      handleCloseModal();
      fetchJobListings(professionFilter, dateFrom);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update job requirement.');
    }
  };


  // Set default date (90 days ago)
  useEffect(() => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() - 90);
    setDateFrom('');
    fetchJobListings('', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to page 1 when view type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [viewType]);

  const currentPageData = getCurrentPageData();

  return (
    <div className="App">
      {/* Common Dashboard Header */}
      <DashboardHeader
        userType={(user?.role || 'hospital')?.toLowerCase()}
        userName={user?.displayName || 'City General Hospital'}
      />

      {/* Main Content */}
      <main className="dashboard-content">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="d-block d-md-flex justify-content-between align-items-center  mb-4">
                <h2 className="dashboard-title mb-2">Job Requirements</h2>
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewType('table')}
                      title="Table View"
                    >
                      <FaList />
                    </button>
                    <button
                      type="button"
                      className={`btn ${viewType === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewType('grid')}
                      title="Grid View"
                    >
                      <FaTh />
                    </button>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddJobClick}
                  >
                    Add Job Requirement
                  </button>
                </div>
              </div>

              {/* Filter Section */}
              <div className="filter-section mb-4">
                <div className="row align-items-end">
                  <div className="col-md-4">
                    <label htmlFor="professionFilter" className="form-label">Profession Type</label>
                    <select
                      className="form-select"
                      id="professionFilter"
                      value={professionFilter}
                      onChange={(e) => {
                        const val = e.target.value;
                        setProfessionFilter(val === '' ? '' : Number(val));
                      }}
                    >
                      <option value="">All Professions</option>
                      {professionOptions.map((p) => (
                        <option key={p.idProfessionType} value={p.idProfessionType}>
                          {p.professionTypeName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="dateFrom" className="form-label">Date From</label>
                    <input
                      type="date"
                      className="form-control"
                      id="dateFrom"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      placeholder="Select date"
                    />
                  </div>
                  <div className="col-md-4">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleApplyFilter}
                    >
                      Apply Filter
                    </button>
                  </div>
                </div>
              </div>



              {/* Job Requirements Table View */}
              {viewType === 'table' && (
                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Posted Date</th>
                            <th>Requirement</th>
                            <th>Type</th>
                            <th>Job Details</th>
                            <th>Total Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="7" className="text-center">Loading...</td>
                            </tr>
                          ) : loadError ? (
                            <tr>
                              <td colSpan="7" className="text-center text-danger">{loadError}</td>
                            </tr>
                          ) : currentPageData.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center">No job requirements found</td>
                            </tr>
                          ) : (
                            currentPageData.map((job) => (
                              <tr key={job.jobID}>
                                <td>
                                  {formatDate(job.createdAt)}
                                </td>
                                <td>{job.professionTypeName || '-'}</td>
                                <td>{job.jobDurationType || '-'}</td>
                                <td>{job.jobDetails || '-'}</td>
                                <td className="text-end">
                                    {job.totalAmount !== null && job.totalAmount !== undefined && job.totalAmount !== ''
                                      ? `Rs. ${formatINRNumber(job.totalAmount)}`
                                      : '-'}
                                  </td>
                                <td>
                                  <span className={getStatusBadgeClass(job.jobStatus || '')}>
                                    {job.jobStatus || '-'}
                                  </span>
                                </td>
                                <td>
                                  {job.jobStatus === "Pending" &&(
                                    <button
                                      type="button"
                                      className="btn btn-link btn-outline-primary p-1 px-2 me-1"
                                      onClick={() => {
                                        handleEditJob(job.jobID);
                                      }}
                                    >
                                      <FaEdit />
                                    </button>
                                  )}
                                 
                                  <button
                                    type="button"
                                    className="btn btn-link btn-outline-primary p-1 px-2 me-1"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleViewJobDetails(job.jobID);
                                    }}
                                    title="View Details"
                                  >
                                    <FaEye />
                                  </button>
                                  {/* DELETE BUTTON — ONLY FOR PENDING STATUS */}
                                  {job.jobStatus === "Pending" && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger p-1 px-2"
                                      onClick={() => handleDeleteJob(job.jobID)}
                                    >
                                      <FaTrash />
                                    </button>
                                  )}
                                </td>

                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Job Requirements Grid View */}
              {viewType === 'grid' && (
                <div className="row g-3">
                  {isLoading ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">Loading...</p>
                        </div>
                      </div>
                    </div>
                  ) : loadError ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-danger">{loadError}</p>
                        </div>
                      </div>
                    </div>
                  ) : currentPageData.length === 0 ? (
                    <div className="col-12">
                      <div className="card">
                        <div className="card-body text-center py-5">
                          <p className="text-muted">No job requirements found</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    currentPageData.map((job) => (
                      <div key={job.jobID} className="col-12 col-md-6 col-lg-4">
                        <div className="card h-100 opportunity-card modern-card">
                          <div className="card-body d-flex flex-column p-0">
                            {/* Header Section */}
                            <div className="card-header-section">
                              <h5 className="card-position-title">{job.professionTypeName || '-'}</h5>
                              <div className="d-flex align-items-center gap-2">
                                <span className={getStatusBadgeClass(job.jobStatus || '')}>
                                  {job.jobStatus || '-'}
                                </span>

                              </div>
                            </div>

                            {/* Info Section */}
                            <div className="card-info-section">
                              <div className="info-item">
                                <div className="info-icon hospital-icon">
                                  <FaCalendarAlt />
                                </div>
                                <div className="info-content">
                                  <div className="info-label">Posted Date</div>
                                  <div className="info-value">
                                    {formatDate(job.createdAt)}
                                  </div>
                                </div>
                              </div>

                              <div className="info-item">
                                <div className="info-icon location-icon">
                                  <FaFileAlt />
                                </div>
                                <div className="info-content">
                                  <div className="info-label">Type</div>
                                  <div className="info-value">{job.jobDurationType || '-'}</div>
                                </div>
                              </div>
                            </div>

                            {/* Divider */}
                            <div className="card-divider"></div>

                            {/* Details Section */}
                            <div className="card-details-section">
                              <div className="detail-item">
                                <FaCalendarAlt className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Job Details</span>
                                  <span className="detail-value">
                                    {job.jobDetails || '-'}
                                  </span>
                                </div>
                              </div>
                              <div className="detail-item">
                                <FaMoneyBillAlt className="detail-icon" />
                                <div className="detail-content">
                                  <span className="detail-label">Total Amount</span>
                                  <span className="detail-value text-end w-100 d-block">
  {job.totalAmount !== null && job.totalAmount !== undefined && job.totalAmount !== ''
    ? `Rs. ${formatINRNumber(job.totalAmount)}`
    : '-'}
</span>
                                </div>
                              </div>
                              <div className="d-flex justify-content-end gap-1">
                                {job.jobStatus === "Pending" && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-outline-primary p-1 px-2"
                                    onClick={() => {
                                      handleEditJob(job.jobID);
                                    }}
                                    title="Edit"
                                  >
                                    <FaEdit />
                                  </button>
                                )}
                               
                                <button
                                  type="button"
                                  className="btn btn-link btn-outline-primary p-1 px-2"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleViewJobDetails(job.jobID);
                                  }}
                                  title="View Details"
                                >
                                  <FaEye />
                                </button>
                                {/* DELETE BUTTON — ONLY FOR PENDING STATUS */}
                                {job.jobStatus === "Pending" && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger p-1 px-2"
                                    onClick={() => {
                                      handleDeleteJob(job.jobID);
                                    }}
                                    title="Delete"
                                  >
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            </div>

                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Pagination - Show for both views */}
              {totalPages > 1 && (
                <nav aria-label="Page navigation" className="mt-4">
                  <ul className="pagination justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                      >
                        Previous
                      </a>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <li
                        key={page}
                        className={`page-item ${page === currentPage ? 'active' : ''}`}
                      >
                        <a
                          className="page-link"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(page);
                          }}
                        >
                          {page}
                        </a>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <a
                        className="page-link"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Add Job Requirement Modal - React Bootstrap Modal */}
      <Modal
        show={showAddJobModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Job Requirement</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleJobFormSubmit}>
          <Modal.Body>
            <div className="row m-0">
              {/* Looking For */}
              <div className="col-md-6 p-2">
                <label htmlFor="lookingFor" className="form-label mb-1">
                  Looking For <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="lookingFor"
                  name="lookingFor"
                  value={jobFormData.lookingFor}
                  onChange={handleJobFormChange}
                  required
                >
                  <option value="">Select Profession</option>
                  {professionOptions.map((p) => (
                    <option key={p.idProfessionType} value={p.idProfessionType}>
                      {p.professionTypeName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Qualification */}
              <div className="col-md-6 p-2">
                <label htmlFor="qualification" className="form-label mb-1">
                  Qualification <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="qualification"
                  name="qualification"
                  value={jobFormData.qualification}
                  onChange={handleJobFormChange}
                  required
                  disabled={isQualificationDisabled}
                >
                  <option value="">Select Qualification</option>
                  {qualificationOptions.map((qual) => (
                    <option key={qual.id} value={qual.id}>
                      {qual.name}
                    </option>
                  ))}
                </select>

              </div>

              {/* Average Years of Experience */}
              <div className="col-md-6 p-2">
                <label htmlFor="experience" className="form-label mb-1">
                  Average Years of Experience
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="experience"
                  name="experience"
                  value={jobFormData.experience}
                  onChange={handleJobFormChange}
                  placeholder="Enter years (0-15)"
                  min="0"
                  max="15"
                />
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="experience" className="form-label mb-1">
                  Period
                </label>
                <div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="period"
                      id="periodShift"
                      value="for a shift"
                      checked={jobFormData.period === "for a shift"}
                      onChange={handleJobFormChange}
                    />
                    <label className="form-check-label" htmlFor="periodShift">
                      For a shift
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="period"
                      id="periodDay"
                      value="for a period"
                      checked={jobFormData.period === "for a period"}
                      onChange={handleJobFormChange}
                    />
                    <label className="form-check-label" htmlFor="periodDay">
                      For a period
                    </label>
                  </div>
                </div>
              </div>



              {/* Start Date */}
              <div className="col-md-3 p-2">
                <label htmlFor="editStartDate" className="form-label mb-1">
                  Start Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="editStartDate"
                  name="startDate"
                  value={jobFormData.startDate}
                  onChange={handleJobFormChange}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>

              {/* End Date - Only show when "For a period" is selected */}
              {jobFormData.period === "for a period" && (
                <div className="col-md-3 p-2">
                  <label htmlFor="editEndDate" className="form-label mb-1">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="editEndDate"
                    name="endDate"
                    value={jobFormData.endDate}
                    onChange={handleJobFormChange}
                    min={
                      jobFormData.startDate
                        ? new Date(new Date(jobFormData.startDate).getTime() + 24 * 60 * 60 * 1000)
                            .toISOString()
                            .split("T")[0]
                        : new Date().toISOString().split("T")[0]
                    }
                    required
                  />
                </div>
              )}

              {jobFormData.period === "for a shift" && (
                <>

                  {/* Start Time */}
                  <div className="col-md-3 p-2">
                    <label htmlFor="editStartTime" className="form-label mb-1">
                      Start Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      id="editStartTime"
                      name="startTime"
                      value={jobFormData.startTime || ''}
                      onChange={handleJobFormChange}
                      min={getMinStartTime()}
                      required
                    />
                  </div>

                  {/* Salary */}
                  <div className="col-md-3 p-2">
                    <label htmlFor="salary" className="form-label mb-1">
                      Hourly Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="salary"
                      name="salary"
                      value={jobFormData.salary}
                      onChange={handleJobFormChange}
                      placeholder="Hourly amount (e.g., 500)"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  {/* Duration */}
                  <div className="col-md-3 p-2">
                    <label htmlFor="editDuration" className="form-label mb-1">
                      Duration (in Hours)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="editDuration"
                      name="duration"
                      value={jobFormData.duration || ''}
                      onChange={handleJobFormChange}
                      min="0"
                      max="12"
                      step="0.25"
                      required
                    />
                  </div>
                </>
              )}
              {/* Total Amount */}
              <div className="col-md-6 p-2">
                <label htmlFor="edittotalPerHours" className="form-label mb-1">
                  Total Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="edittotalPerHours"
                  name="totalPerHours"
                  value={jobFormData.totalPerHours || ''}
                  onChange={handleJobFormChange}
                  min="0"
                  step="0.01"
                  readOnly={jobFormData.period === 'for a shift'}
                  required
                />
                {jobFormData.period === 'for a shift' && (
                  <small className="form-text text-muted">Auto-calculated: hourly amount × duration</small>
                )}
              </div>

              {/* Summary */}
              <div className="col-md-12 p-2">
                <label className="form-label mb-1">Job details</label>
                <ReactQuill
                  theme="snow"
                  value={jobFormData.remarks}
                  className="experience-details-editor"
                  onChange={(value) => {
                    // Limit to 1000 characters (HTML tags not counted in limit)
                    const textContent = value.replace(/<[^>]*>/g, '');
                    if (textContent.length <= 1000) {
                      setJobFormData(prev => ({ ...prev, remarks: value }));
                    }
                  }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline', 'strike',
                    'list', 'bullet',
                    'link'
                  ]}
                  style={{ minHeight: '150px', marginBottom: '50px' }}
                />
              </div>

              {/* Suspend Checkbox */}
              {user?.role?.toLowerCase() === "admin" && (
                <div className="col-md-12 p-2">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="suspend"
                      name="suspend"
                      checked={jobFormData.suspend}
                      onChange={handleJobFormChange}
                    />
                    <label className="form-check-label" htmlFor="suspend">
                      Suspend - Hide this entry to be viewed by Medical Professional
                    </label>
                  </div>
                </div>
              )}

            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Add Requirement
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Edit Job Requirement Modal - React Bootstrap Modal */}
      <Modal
        show={showEditJobModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Job Requirement</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleEditJobFormSubmit}>
          <Modal.Body>
            <div className="row m-0">
              {/* Looking For */}
              <div className="col-md-6 p-2">
                <label htmlFor="editLookingFor" className="form-label mb-1">
                  Looking For <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="editLookingFor"
                  name="lookingFor"
                  value={jobFormData.lookingFor}
                  onChange={handleJobFormChange}
                  required
                >
                  <option value="">Select Profession</option>
                  {professionOptions.map((p) => (
                    <option key={p.idProfessionType} value={p.idProfessionType}>
                      {p.professionTypeName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Qualification */}
              <div className="col-md-6 p-2">
                <label htmlFor="editQualification" className="form-label mb-1">
                  Qualification <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="editQualification"
                  name="qualification"
                  value={jobFormData.qualification}
                  onChange={handleJobFormChange}
                  required
                  disabled={isQualificationDisabled}
                >
                  <option value="">Select Qualification</option>
                  {qualificationOptions.map((qual) => (
                    <option key={qual.id} value={qual.id}>
                      {qual.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Average Years of Experience */}
              <div className="col-md-6 p-2">
                <label htmlFor="editExperience" className="form-label mb-1">
                  Average Years of Experience
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="editExperience"
                  name="experience"
                  value={jobFormData.experience}
                  onChange={handleJobFormChange}
                  placeholder="Enter years (0-15)"
                  min="0"
                  max="15"
                />
              </div>
              <div className="col-md-6 p-2">
                <label htmlFor="editPeriod" className="form-label mb-1">Period</label>
                <div className="form-check form-check-inline">
                  <input className="form-check-input" type="radio" name="period" id="periodShift" value="for a shift" checked={jobFormData.period === "for a shift"} onChange={handleJobFormChange} />
                  <label className="form-check-label" htmlFor="periodShift">For a shift</label>
                </div>
                <div className="form-check form-check-inline">
                  <input className="form-check-input" type="radio" name="period" id="periodDay" value="for a period" checked={jobFormData.period === "for a period"} onChange={handleJobFormChange} />
                  <label className="form-check-label" htmlFor="periodDay">For a period</label>
                </div>
              </div>


              {/* Start Date */}
              <div className="col-md-3 p-2">
                <label htmlFor="editStartDate" className="form-label mb-1">
                  Start Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="editStartDate"
                  name="startDate"
                  value={jobFormData.startDate}
                  onChange={handleJobFormChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              {/* End Date - Only show when "For a period" is selected */}
              {jobFormData.period === "for a period" && (
                <div className="col-md-3 p-2">
                  <label htmlFor="editEndDate" className="form-label mb-1">
                    End Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="editEndDate"
                    name="endDate"
                    value={jobFormData.endDate}
                    onChange={handleJobFormChange}
                    min={getMinEndDate(jobFormData.startDate)}
                    required
                  />
                </div>
              )}
              {jobFormData.period === "for a shift" && (
                <>
                  {/* Start Time */}
                  <div className="col-md-3 p-2">
                    <label htmlFor="editStartTime" className="form-label mb-1">
                      Start Time <span className="text-danger">*</span>
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      id="editStartTime"
                      name="startTime"
                      value={jobFormData.startTime || ''}
                      onChange={handleJobFormChange}
                      required
                    />
                  </div>

                  {/* Salary */}
                  <div className="col-md-3 p-2">
                    <label htmlFor="salary" className="form-label mb-1">
                      Hourly Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="salary"
                      name="salary"
                      value={jobFormData.salary}
                      onChange={handleJobFormChange}
                      placeholder="Hourly amount (e.g., 500)"
                      min="0"
                      step="0.01"
                    />
                  </div>




                  {/* Duration */}
                  <div className="col-md-3 p-2">
                    <label htmlFor="editDuration" className="form-label mb-1">
                      Duration (in Hours)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="editDuration"
                      name="duration"
                      value={jobFormData.duration || ''}
                      onChange={handleJobFormChange}
                      min="0"
                      max="12"
                      required
                    />
                  </div>
                </>
              )}

              {/* Total Amount */}
              <div className="col-md-6 p-2">
                <label htmlFor="edittotalPerHours" className="form-label mb-1">
                  Total Amount <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="edittotalPerHours"
                  name="totalPerHours"
                  value={jobFormData.totalPerHours || ''}
                  onChange={handleJobFormChange}
                  min="0"
                  required
                />
              </div>

              {/* Summary */}
              <div className="col-md-12 p-2">
                <label className="form-label mb-1">Job details</label>
                <ReactQuill
                  theme="snow"
                  value={jobFormData.remarks}
                  className="experience-details-editor"
                  onChange={(value) => {
                    // Limit to 1000 characters (HTML tags not counted in limit)
                    const textContent = value.replace(/<[^>]*>/g, '');
                    if (textContent.length <= 1000) {
                      setJobFormData(prev => ({ ...prev, remarks: value }));
                    }
                  }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                  formats={[
                    'header',
                    'bold', 'italic', 'underline', 'strike',
                    'list', 'bullet',
                    'link'
                  ]}
                  style={{ minHeight: '100px' }}
                />
              </div>



              {/* Suspend Checkbox */}
              <div className="col-md-12 p-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editSuspend"
                    name="suspend"
                    checked={jobFormData.suspend}
                    onChange={handleJobFormChange}
                  />
                  <label className="form-check-label" htmlFor="editSuspend">
                    Suspend - Hide this entry to be viewed by Medical Professional
                  </label>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Requirement
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* View Responses Modal - React Bootstrap Modal */}
      <Modal
        show={showResponsesModal}
        onHide={handleCloseResponsesModal}
        centered
        size="xl"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Responses
            {viewingJobId && (
              <span className="ms-2 text-muted" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                - {jobRequirements.find(j => Number(j.jobID) === Number(viewingJobId))?.jobDetails || jobRequirements.find(j => Number(j.jobID) === Number(viewingJobId))?.professionTypeName}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingJobId && (() => {
            const responses = getJobResponses(viewingJobId);
            const job = jobRequirements.find(j => j.id === viewingJobId);

            if (!job || job.responses === 0 || responses.length === 0) {
              return (
                <div className="text-center py-5">
                  <p className="text-muted">No responses found for this job requirement.</p>
                </div>
              );
            }

            return (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">Professional Name</th>
                      <th scope="col">Qualification</th>
                      <th scope="col">Experience</th>
                      <th scope="col">Phone no.</th>
                      <th scope="col">Applied Date</th>
                      <th scope="col">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((response) => (
                      <tr key={response.id}>
                        <td>
                          <strong>{response.professionalName}</strong>
                          <br />
                          <small className="text-muted">{response.email}</small>
                        </td>
                        <td>{response.qualification}</td>
                        <td>{response.experience}</td>
                        <td>
                          {response.mobile}
                        </td>
                        <td>{formatDate(response.appliedDate)}</td>
                        <td>
                          <span className={`badge ${response.status === 'Shortlisted' ? 'bg-success' :
                            response.status === 'Rejected' ? 'bg-danger' :
                              'bg-warning text-dark'
                            }`}>
                            {response.status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary me-1"
                            onClick={() => {
                              alert(`Viewing profile of ${response.professionalName}`);
                            }}
                          >
                            View Profile
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseResponsesModal}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* View Job Details Modal */}
      <Modal
        show={showViewDetailsModal}
        onHide={handleCloseViewDetailsModal}
        centered
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Job Requirement Details{' '}
            <small>
              {viewJobData?.createdAt ? `(${formatDate(viewJobData.createdAt)})` : ''}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isViewLoading && (
            <div className="text-center py-4">
              <p className="text-muted">Loading...</p>
            </div>
          )}
          {!isViewLoading && viewError && (
            <div className="text-center py-4">
              <p className="text-danger">{viewError}</p>
            </div>
          )}

          {!isViewLoading && !viewError && viewJobData && (
            <div className="job-details">
              <div className="row m-0">

                {/* Profession */}
                <div className="col-md-6 p-2">
                  <label className="form-label mb-1">Looking For</label>
                  <p className="form-control m-0">
                    {getProfessionName(viewJobData.idProfessionType)}
                  </p>
                </div>

                {/* Qualification */}
                <div className="col-md-6 p-2">
                  <label className="form-label mb-1">Qualification</label>
                  <p className="form-control m-0">
                    {getQualificationName(viewJobData.idProfessionType, viewJobData.idQualification)}
                  </p>

                </div>

                {/* Experience */}
                <div className="col-md-6 p-2">
                  <label className="form-label mb-1">Average Years of Experience</label>
                  <p className="form-control m-0">{viewJobData.experienceInYears ?? '-'}</p>
                </div>

                {/* Job Duration Type */}
                <div className="col-md-6 p-2">
                  <label className="form-label mb-1">Period</label>
                  <p className="form-control m-0">{viewJobData.jobDurationType || '-'}</p>
                </div>

                {/* Start Date */}
                <div className="col-md-3 p-2">
                  <label className="form-label mb-1">Start Date</label>
                  <p className="form-control m-0">{formatDate(viewJobData.dateFrom)}</p>
                </div>

                {/* End Date – Only for period */}
                {viewJobData.jobDurationType === "for a period" && (
                  <div className="col-md-3 p-2">
                    <label className="form-label mb-1">End Date</label>
                    <p className="form-control m-0">{formatDate(viewJobData.dateTo)}</p>
                  </div>
                )}

                {/* Shift Fields */}
                {viewJobData.jobDurationType === "for a shift" && (
                  <>
                    <div className="col-md-3 p-2">
                      <label className="form-label mb-1">Start Time</label>
                      <p className="form-control m-0">{viewJobData.timeFrom || '-'}</p>
                    </div>

                    <div className="col-md-3 p-2">
                      <label className="form-label mb-1">Duration (Hours)</label>
                      <p className="form-control m-0">{viewJobData.durationInHours ?? '-'}</p>
                    </div>

                    <div className="col-md-3 p-2">
                      <label className="form-label mb-1">Hourly Amount</label>
                      <p className="form-control m-0">
  {viewJobData.hourlyRateByHospital !== null && viewJobData.hourlyRateByHospital !== undefined
    ? `Rs. ${formatINRNumber(viewJobData.hourlyRateByHospital)}`
    : '-'}
</p>
                    </div>
                  </>
                )}

                {/* Total Amount */}
                <div className="col-md-6 p-2">
                  <label className="form-label mb-1">Total Amount</label>
                  <p className="form-control m-0">
  {viewJobData.jobDurationType === "for a shift" &&
  viewJobData.durationInHours !== null &&
  viewJobData.durationInHours !== undefined &&
  viewJobData.hourlyRateByHospital !== null &&
  viewJobData.hourlyRateByHospital !== undefined
    ? `Rs. ${formatINRNumber(viewJobData.durationInHours * viewJobData.hourlyRateByHospital)}`
    : viewJobData.periodTotalAmountByHospital !== null &&
      viewJobData.periodTotalAmountByHospital !== undefined
      ? `Rs. ${formatINRNumber(viewJobData.periodTotalAmountByHospital)}`
      : '-'}
</p>
                </div>

                {/* Remarks */}
                <div className="col-md-12 p-2">
                  <label className="form-label mb-1">Job Details</label>
                  <div
                    className="form-control"
                    style={{ minHeight: "80px", whiteSpace: "pre-wrap" }}
                    dangerouslySetInnerHTML={{ __html: viewJobData.remarks || '-' }}
                  />
                </div>

              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCloseViewDetailsModal}
          >
            Close
          </button>
          {viewingDetailsJobId && viewJobData && (viewJobData.jobStatus.toLowerCase() === 'pending' || viewJobData.jobStatus.toLowerCase() === 'rejected' || viewJobData.jobStatus.toLowerCase() === 'deleted') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                handleCloseViewDetailsModal();
                handleEditJob(viewingDetailsJobId);
              }}
            >
              Edit
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default HospitalDashboard;

